import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import FirestoreService from '../assets/js/firestore_instance';
import { useNavigate } from 'react-router-dom';
import { helix } from 'ldrs';
import { motion, AnimatePresence } from 'framer-motion';
import useEnv from "../hooks/useEnv";

helix.register();

const AnimatedText = ({ text }) => (
  <motion.div
    key={text}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 1 }}
    className="w-[300px] text-left"
  >
    <h1 className="text-2xl text-white font-bold">{text}</h1>
  </motion.div>
);

const Auth = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const db = FirestoreService.getFirestore();
  const envUrl = useEnv();

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const loadingMessages = [
    "Retrieving your Groups",
    "Collecting People Data",
    "Creating stunning Profiles",
    "Building gorgeous dashboards",
    "Finding your lost socks",
    "Assigning people to profiles",
    "Locating the Holy Grail",
    "Creating the perfect group",
    "Bringing balance to the Force",
    "Assembling the Avengers",
    "Warping to hyperspace",
    "Collecting group schedules",
    "Bringing the One Ring to Mordor",
    "...you have a lot of groups",
    "Hacking the Matrix",
    "Powering up the Hyperdrive",
    "Analyzing Midi-chlorians",
    "...honestly, this is taking a while",
    "Something might be wrong",
    "You might want to try again",
    "Or give us a call",
    "And just so you know...",
    "We're proud of you for waiting this long",
    "Thats the commitment we like to see out of church leaders",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => 
        (prevIndex + 1) % loadingMessages.length
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const authCheck = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/sign-in');
        return;
      }

      const docRef = doc(db, "admin_users", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const clientId = 'dc7944cd543cc5b66e1b92602bcc5f83dc7022871817b4ca2856589180876e18';
        const clientSecret = '2c175d084316865441feaa70ebf71e77c2256c6a2a6aa2d875a6ebafff01a4e3';
        // const redirectUri = 'http://127.0.0.1:5001/joltchat/us-central1/pcoIntegration'; // dev
        const redirectUri = `${envUrl}/pcoIntegration`; // prod
        const scopes = ['people', 'groups'];

        const authorize = () => {
          const authorizationUrl = `https://api.planningcenteronline.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scopes.join(' ')}`;
          window.location.href = authorizationUrl;
        };

        const handleCallback = async () => {
          const urlParams = new URLSearchParams(window.location.search);
          const authorizationCode = urlParams.get('code');

          if (!authorizationCode) {
            console.error('Authorization code not found');
            return;
          }

          try {
            const response = await fetch('https://api.planningcenteronline.com/oauth/token', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: `grant_type=authorization_code&code=${authorizationCode}&client_id=${clientId}&client_secret=${clientSecret}&redirect_uri=${redirectUri}`,
            });
            const data = await response.json();
            const accessToken = data.access_token;

            const peopleResponse = await fetch('https://api.planningcenteronline.com/api/v2/people', {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
            });
            const peopleData = await peopleResponse.json();
            const orgID = peopleData.data[0].id;

            await setDoc(docRef, { organizationId: [orgID] }, { merge: true });
            setLoading(false);
          } catch (error) {
            console.error('Error accessing API:', error);
            setError(error);
          }
        };

        if (window.location.search.includes('code=')) {
          handleCallback();
        } else {
          authorize();
        }
      } else {
        navigate('/sign-in');
      }
    });

    return () => authCheck();
  }, [auth, db, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen dashboard-main-color">
        <div className="flex flex-row items-center space-x-8">
          <l-helix
            size="60"
            speed="1.75"
            color="#ff7969" 
          />
          <div className="pl-5 pb-9 mt-9">
            <AnimatePresence mode="wait">
              <AnimatedText text={loadingMessages[currentMessageIndex]} />
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return null;
};

export default Auth;